import { useMutation, useQuery } from '@tanstack/react-query'
import { ActionBar, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { PopulationFields } from '../../../components/PopulationFields'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { trpc } from '../../../utils/trpc'
import { usePecStore } from '../store'

/**
 * @fileoverview PEC Population Tab
 *
 * Select target population for PEC cycles
 */

interface PopulationFormData {
	population: {
		hierarchyLevels: number[]
		roles: Array<{ id: string; label: string }>
		blacklistedHierarchyRoles: Array<{ id: string; label: string }>
		whitelistedUsers: Array<{ id: string; externalId: string; label: string }>
		blacklistedUsers: Array<{ id: string; externalId: string; label: string }>
	}
}

export const PecPopulationTab = () => {
	const { pecId } = useParams<{ pecId: string }>()
	const { organization } = sessionStore()
	const organizationId = organization?.id
	const navigate = useNavigate()
	const { pecCycle, useEditPecCycle } = usePecStore()
	const { editPecCycle, isLoading: isSaving } = useEditPecCycle(pecId)

	const form = useForm<PopulationFormData>({
		defaultValues: {
			population: {
				hierarchyLevels: [],
				roles: [],
				blacklistedHierarchyRoles: [],
				whitelistedUsers: [],
				blacklistedUsers: [],
			},
		},
	})

	// Mutations for population operations
	const createPopulationMutation = useMutation({
		...trpc.client.populations.create.mutationOptions(),
	})

	const editPopulationMutation = useMutation({
		...trpc.client.populations.edit.mutationOptions(),
	})

	// Fetch existing population data if populationId exists
	const { data: populationData, isLoading: isLoadingPopulation } = useQuery({
		...trpc.client.populations.getFullById.queryOptions({
			id: pecCycle?.populationId || '',
		}),
		enabled: !!pecCycle?.populationId,
	})

	// Load population data into form
	useEffect(() => {
		if (populationData) {
			form.reset({
				population: {
					hierarchyLevels: populationData.hierarchyLevels || [],
					roles:
						populationData.roles?.map((r) => ({
							id: r.id,
							label: r.displayName || r.name,
						})) || [],
					blacklistedHierarchyRoles:
						populationData.blacklistedHierarchyRoles?.map((r) => ({
							id: r.id,
							label: r.displayName || r.name,
						})) || [],
					whitelistedUsers:
						populationData.whitelistedUsers?.map((u) => ({
							id: u.id,
							externalId: u.externalId,
							label: `${u.firstName} ${u.lastName}`,
						})) || [],
					blacklistedUsers:
						populationData.blacklistedUsers?.map((u) => ({
							id: u.id,
							externalId: u.externalId,
							label: `${u.firstName} ${u.lastName}`,
						})) || [],
				},
			})
		}
	}, [populationData, form])

	const handleSubmit = form.handleSubmit(async (data) => {
		const populationInput = {
			hierarchyLevels: data.population.hierarchyLevels,
			roles: data.population.roles.map((r) => r.id),
			blacklistedHierarchyRoles: data.population.blacklistedHierarchyRoles.map((r) => r.id),
			whitelistedUsers: data.population.whitelistedUsers.map((u) => u.id),
			blacklistedUsers: data.population.blacklistedUsers.map((u) => u.id),
		}

		// Create or update population
		let populationId = pecCycle?.populationId

		if (populationId) {
			// Update existing population
			await editPopulationMutation.mutateAsync({
				id: populationId,
				...populationInput,
			})
		} else {
			// Create new population
			const newPopulation = await createPopulationMutation.mutateAsync(populationInput)
			populationId = newPopulation.id
		}

		// Update PEC cycle with populationId
		await editPecCycle({ populationId })
	})

	// Use pecId as cycleId substitute
	const contextId = useMemo(() => pecId || '', [pecId])

	if (isLoadingPopulation) {
		return <SpinnerWithBackground />
	}

	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit} className='relative pb-12 mt-6 w-full'>
				<div className='px-6 max-w-4xl'>
					<Text variant='medium' weight='semibold' className='mb-4'>
						Population Selection
					</Text>
					<Text variant='small' className='text-neutral-600 mb-6'>
						Define which employees will participate in the PEC cycle. All selected participants will rate each other.
					</Text>

					<PopulationFields
						name='population'
						projection={{
							title: false,
							hierarchyTitle: false,
							hierarchyLevels: false,
							blacklistedHierarchyRoles: false,
							rolesTitle: false,
							roles: false,
							reviewersTitle: true,
							whitelistedUsers: true,
							blacklistedUsers: true,
						}}
						cycleId={contextId}
						organizationId={organizationId || ''}
					/>
				</div>
				<ActionBar
					rightActions={[
						{
							variant: 'secondary' as const,
							label: 'Cancel',
							onClick: () => navigate('/pec'),
						},
						{
							variant: 'primary' as const,
							label: 'Save',
							onClick: handleSubmit,
							disabled: isSaving,
						},
					]}
				/>
			</form>
		</FormProvider>
	)
}
